import type {
  AdminDashboard,
  AdminUserDetail,
  AdminUserRow,
  AiCostDaily,
  AiCosts,
  AiCostsParams,
  AiCostsSummary,
  AiUsageSortBy,
  AiUserUsageRow,
  AdminPlan,
  AdminAuditEntry,
  AdminCreditPack,
  AiCreditLedgerEntry,
  AiStrategySummary,
  AiUserActivity,
  AdminChatTurn,
  AdminStrategyChat,
  AdminUserAccount,
  AdminUserFill,
  AdminUserFillsPage,
  ApprovalStatus,
  FillSide,
  UserFillsParams,
  BacktestCharts,
  DailyCount,
  SubscriptionSummary,
  InfraMetrics,
  InfraOverview,
  InfraWindow,
  LoginActivity,
  LoginEvent,
  SessionActivity,
  SessionItem,
  MetricSeries,
} from '@core/api/adminTypes';

/**
 * Deterministic mock dataset for the admin backoffice. A seeded PRNG keeps every render stable
 * (same user list, same charts) so the demo doesn't flicker. Money/volume are emitted as decimal
 * STRINGS to match the wire contract exactly.
 */

// ─── Seeded PRNG (mulberry32) ───────────────────────────────────────────────────────────
function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

const money = (n: number): string => n.toFixed(2);

// ─── Date helpers ───────────────────────────────────────────────────────────────────────
function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function eachDay(from: string, to: string): string[] {
  const out: string[] = [];
  const start = new Date(from + 'T00:00:00');
  const end = new Date(to + 'T00:00:00');
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) out.push(ymd(new Date(d)));
  return out.slice(0, 366);
}

// ─── User universe ──────────────────────────────────────────────────────────────────────
const FIRST = ['James', 'Maria', 'Wei', 'Aisha', 'Liam', 'Sofia', 'Noah', 'Priya', 'Lucas', 'Emma',
  'Mateo', 'Yuki', 'Omar', 'Chloe', 'Ethan', 'Ingrid', 'Diego', 'Hana', 'Carlos', 'Zoe',
  'Ravi', 'Nadia', 'Tomas', 'Leah', 'Andre', 'Mei', 'Felix', 'Sara', 'Victor', 'Anna'];
const LAST = ['Carter', 'Lopez', 'Zhang', 'Khan', 'Murphy', 'Rossi', 'Kim', 'Patel', 'Silva', 'Novak',
  'Garcia', 'Tanaka', 'Haddad', 'Dubois', 'Brown', 'Larsen', 'Mendez', 'Sato', 'Reyes', 'Walsh',
  'Sharma', 'Ali', 'Horak', 'Cohen', 'Moreau', 'Chen', 'Weber', 'Olsen', 'Petrov', 'Schmidt'];
const SYMBOLS = ['ES', 'NQ', 'CL', 'GC', 'MES', 'MNQ', 'RTY', 'YM', '6E', 'ZB', 'SI', 'NG'];
const TIMEZONES = ['America/New_York', 'America/Chicago', 'America/Los_Angeles', 'Europe/London', 'Asia/Singapore'];

const STATUS_CYCLE: ApprovalStatus[] = [
  'Approved', 'Approved', 'Approved', 'Approved', 'Approved', 'Approved',
  'Pending', 'Pending', 'Rejected',
];

const TOTAL_USERS = 138;

export interface MockUser {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  approvalStatus: ApprovalStatus;
  createdAt: Date;
  decidedAt: Date | null;
  isDeleted: boolean;
  phoneNumber: string;
  timeZone: string;
  cqgConnected: boolean;
}

function buildUsers(): MockUser[] {
  const rng = mulberry32(0xA11CE);
  const now = Date.now();
  const users: MockUser[] = [];
  for (let i = 0; i < TOTAL_USERS; i++) {
    const first = FIRST[i % FIRST.length] as string;
    const last = LAST[(i * 7) % LAST.length] as string;
    const status = STATUS_CYCLE[i % STATUS_CYCLE.length] as ApprovalStatus;
    // Spread sign-ups across the last ~14 months.
    const createdAt = new Date(now - Math.floor(rng() * 430) * 86400_000 - Math.floor(rng() * 86400_000));
    const decidedAt = status === 'Pending'
      ? null
      : new Date(createdAt.getTime() + Math.floor(rng() * 5 + 1) * 86400_000);
    users.push({
      userId: `usr_${(1000 + i).toString(36)}${i}`,
      email: `${first}.${last}`.toLowerCase().replace(/[^a-z.]/g, '') + `${i}@example.com`,
      firstName: first,
      lastName: last,
      approvalStatus: status,
      createdAt,
      decidedAt,
      isDeleted: false,
      phoneNumber: `+1 (${200 + (i % 700)}) ${String(100 + (i % 900)).padStart(3, '0')}-${String(1000 + (i * 13) % 9000).padStart(4, '0')}`,
      timeZone: TIMEZONES[i % TIMEZONES.length] as string,
      cqgConnected: status === 'Approved' && rng() > 0.45,
    });
  }
  return users;
}

let _users: MockUser[] | null = null;
export function mockUsers(): MockUser[] {
  if (!_users) _users = buildUsers();
  return _users;
}

/** Per-user deterministic trading profile within a date window. */
function userTrading(u: MockUser, from: string, to: string) {
  const rng = mulberry32(hashString(u.userId));
  const days = eachDay(from, to);
  const approved = u.approvalStatus === 'Approved';
  // Pending/Rejected users barely trade (or not at all).
  const intensity = approved ? 0.55 + rng() * 0.45 : rng() * 0.12;

  // `fills` per day as well as per range: mockFillRows generates exactly this many rows for the day, so
  // the fill list, the Fills KPI and the per-symbol fills column can never disagree.
  const daily: { date: string; net: number; trades: number; fills: number }[] = [];
  let cumulative = 0;
  let totalTrades = 0;
  let gains = 0;
  let losses = 0;
  let wins = 0;
  let lossCount = 0;
  let volume = 0;
  let fills = 0;

  for (const date of days) {
    const traded = rng() < intensity;
    if (!traded) { daily.push({ date, net: 0, trades: 0, fills: 0 }); continue; }
    const dayTrades = 1 + Math.floor(rng() * 6 * intensity);
    let dayNet = 0;
    let dayFills = 0;
    for (let t = 0; t < dayTrades; t++) {
      const win = rng() < (approved ? 0.52 : 0.4);
      const mag = (50 + rng() * 950) * (approved ? 1 : 0.4);
      const pnl = win ? mag : -mag * (0.8 + rng() * 0.6);
      dayNet += pnl;
      if (pnl >= 0) { gains += pnl; wins++; } else { losses += -pnl; lossCount++; }
      volume += Math.round(1 + rng() * 8);
      // At least 2 — an episode takes a fill to open and a fill to close — plus scale-ins/outs.
      dayFills += 2 + Math.floor(rng() * 3);
    }
    fills += dayFills;
    cumulative += dayNet;
    totalTrades += dayTrades;
    daily.push({ date, net: dayNet, trades: dayTrades, fills: dayFills });
  }

  const netPnl = gains - losses;
  const activeDays = daily.filter(d => d.trades > 0).length;
  const lastActiveDate = [...daily].reverse().find(d => d.trades > 0)?.date ?? null;

  return {
    daily, totalTrades, gains, losses, netPnl, wins, lossCount, volume, fills,
    activeDays,
    lastActivityAt: lastActiveDate ? new Date(lastActiveDate + 'T16:32:00').toISOString() : null,
    fees: totalTrades * (2.1 + rng() * 1.4),
    openPositions: approved && rng() > 0.6 ? 1 + Math.floor(rng() * 2) : 0,
    cumulative,
  };
}

// ─── Dashboard ──────────────────────────────────────────────────────────────────────────
export function mockDashboard(from: string, to: string): AdminDashboard {
  const users = mockUsers();
  const days = eachDay(from, to);
  const inRange = (d: Date) => ymd(d) >= from && ymd(d) <= to;

  const newInRange = users.filter(u => inRange(u.createdAt));
  const approved = users.filter(u => u.approvalStatus === 'Approved').length;
  const pending = users.filter(u => u.approvalStatus === 'Pending').length;
  const rejected = users.filter(u => u.approvalStatus === 'Rejected').length;

  // Registrations daily (sign-ups whose createdAt lands on each day).
  const regByDay = new Map<string, number>(days.map(d => [d, 0]));
  for (const u of users) {
    const k = ymd(u.createdAt);
    if (regByDay.has(k)) regByDay.set(k, (regByDay.get(k) ?? 0) + 1);
  }
  const registrationsDaily: DailyCount[] = days.map(d => ({ date: d, count: regByDay.get(d) ?? 0 }));

  // Aggregate trading across the approved user base.
  let trades = 0, fills = 0, volume = 0, netPnl = 0, gains = 0, losses = 0, wins = 0, lossCount = 0;
  let balance = 0;
  const dauByDay = new Map<string, Set<string>>(days.map(d => [d, new Set<string>()]));
  let activeTraders = 0;

  for (const u of users) {
    const tr = userTrading(u, from, to);
    if (tr.totalTrades > 0) activeTraders++;
    trades += tr.totalTrades;
    fills += tr.fills;
    volume += tr.volume;
    netPnl += tr.netPnl;
    gains += tr.gains;
    losses += tr.losses;
    wins += tr.wins;
    lossCount += tr.lossCount;
    balance += 25000 + tr.cumulative;
    for (const d of tr.daily) if (d.trades > 0) dauByDay.get(d.date)?.add(u.userId);
  }

  const dailyActiveUsers: DailyCount[] = days.map(d => ({ date: d, count: dauByDay.get(d)?.size ?? 0 }));
  const activeUsers = new Set(days.flatMap(d => [...(dauByDay.get(d) ?? [])])).size;
  const winRate = wins + lossCount > 0 ? wins / (wins + lossCount) : 0;
  const cqgConnectedUsers = users.filter(u => u.cqgConnected).length;

  return {
    from, to,
    newRegistrations: newInRange.length,
    approvedUsers: approved,
    pendingUsers: pending,
    rejectedUsers: rejected,
    registrationsDaily,
    activeTraders,
    activeUsers,
    dailyActiveUsers,
    funnelRegistered: users.length,
    funnelApproved: approved,
    funnelFirstTrade: users.filter(u => u.cqgConnected || u.approvalStatus === 'Approved').length - Math.floor(approved * 0.18),
    trades,
    fills,
    volume: money(volume),
    netPnl: money(netPnl),
    totalGains: money(gains),
    totalLosses: money(losses),
    winRate: winRate.toFixed(4),
    wins,
    losses: lossCount,
    accumulatedBalance: money(balance),
    cqgConnectedUsers,
    cqgAccounts: cqgConnectedUsers + Math.floor(cqgConnectedUsers * 0.3),
  };
}

// ─── Users list row ─────────────────────────────────────────────────────────────────────
export function mockUserRow(u: MockUser, from: string, to: string): AdminUserRow {
  const tr = userTrading(u, from, to);
  return {
    userId: u.userId,
    email: u.email,
    approvalStatus: u.approvalStatus,
    createdAt: u.createdAt.toISOString(),
    lastActivityAt: tr.lastActivityAt,
    trades: tr.totalTrades,
    volume: money(tr.volume),
    netPnl: money(tr.netPnl),
    gains: money(tr.gains),
    losses: money(tr.losses),
    isDisabled: false, // overridden per-session by mockApi from its disabled set
    ...mockLogins(u),
  };
}

/** The roster's Last login, read off the same mock sessions the deep-dive shows, so the two agree. */
function mockLogins(u: MockUser): Pick<AdminUserRow, 'lastLoginAt'> {
  return { lastLoginAt: mockSessionActivity(u).lastSeenAt };
}

/** The mock session log's first day: the deep-dive card and the roster columns both count from it. */
export const MOCK_SESSIONS_SINCE = '2026-10-20';

// ─── Fills (v2_trades) ──────────────────────────────────────────────────────────────────

/**
 * The symbols a user trades. Seeded independently of the deep-dive's rng stream so the generated fill
 * rows and the per-symbol table always draw from the same set — otherwise the fills column would not
 * reconcile with the fill list it is supposed to summarise.
 */
function chosenSymbols(u: MockUser): string[] {
  const rng = mulberry32(hashString(u.userId + 'symbols'));
  const count = 2 + Math.floor(rng() * 4);
  return [...SYMBOLS].sort(() => rng() - 0.5).slice(0, count);
}

/** Account keys, shared by mockAccounts and mockFillRows so a fill's account always exists. */
function cqgAccountName(u: MockUser): string { return String(3700000 + (hashString(u.userId) % 99999)); }
function rithmicAccountName(u: MockUser): string { return `RITH-${100 + (hashString(u.userId) % 800)}`; }
function hasRithmic(u: MockUser): boolean { return hashString(u.userId + 'rith') % 3 === 0; }

function accountNames(u: MockUser): string[] {
  const names = ['Manual'];
  if (u.cqgConnected) names.push(cqgAccountName(u));
  if (hasRithmic(u)) names.push(rithmicAccountName(u));
  return names;
}

/**
 * Every execution the user booked in the window, NEWEST FIRST — the mock of `v2_trades`.
 *
 * Exactly `userTrading().daily[].fills` rows per day, so the length of this list is the Fills KPI and
 * grouping it by symbol is the Fills column. Nothing is drawn twice from different streams, which is
 * the only way a mock can demonstrate the reconciliation the real backend guarantees.
 *
 * Legs alternate open → reduce, modelling plain long round-turns: the opening leg carries NO P&L (null,
 * the same as the real table, which only resolves money on the reducing leg) and the reducing one does.
 */
export function mockFillRows(u: MockUser, from: string, to: string): AdminUserFill[] {
  const tr = userTrading(u, from, to);
  const rng = mulberry32(hashString(u.userId + 'fills'));
  const symbols = chosenSymbols(u);
  const accounts = accountNames(u);
  const rows: AdminUserFill[] = [];

  for (const d of tr.daily) {
    if (d.fills === 0) continue;
    // Walk forward through the session so fills within a day are in execution order.
    let minute = 9 * 60 + 30 + Math.floor(rng() * 45);
    for (let i = 0; i < d.fills; i++) {
      minute += 1 + Math.floor(rng() * 24);
      const reducing = i % 2 === 1;
      const net = reducing ? (rng() - 0.45) * 320 : null;
      const fee = 1.8 + rng() * 2.6;
      // ~1 in 40 rows has no side: legacy/imported data whose stored value falls outside the enum. Kept
      // in the mock on purpose so the "—" branch is visible in the demo rather than only in a unit test.
      const side: FillSide | null = rng() < 0.025 ? null : (reducing ? 'Short' : 'Long');
      rows.push({
        id: `fill-${u.userId}-${d.date}-${i}`,
        executedAt: new Date(Date.parse(`${d.date}T00:00:00Z`) + minute * 60_000).toISOString(),
        account: accounts[Math.floor(rng() * accounts.length)] ?? 'Manual',
        symbol: symbols[Math.floor(rng() * symbols.length)] ?? 'ES',
        side,
        quantity: 1 + Math.floor(rng() * 4),
        fee: money(fee),
        grossPnl: net === null ? null : money(net + fee),
        netPnl: net === null ? null : money(net),
      });
    }
  }

  return rows.reverse();
}

/** One page of the fill list, narrowed the same way the real endpoint narrows it. */
export function mockUserFills(u: MockUser, params: UserFillsParams): AdminUserFillsPage {
  const accounts = params.accounts?.length ? new Set(params.accounts) : null;
  const matching = mockFillRows(u, params.from, params.to).filter(f =>
    (!params.symbol || f.symbol === params.symbol) &&
    (!accounts || accounts.has(f.account)));
  const start = (params.page - 1) * params.pageSize;
  return {
    items: matching.slice(start, start + params.pageSize),
    total: matching.length,
    page: params.page,
    pageSize: params.pageSize,
  };
}

// ─── User detail ────────────────────────────────────────────────────────────────────────
export function mockUserDetail(u: MockUser, from: string, to: string): AdminUserDetail {
  const rng = mulberry32(hashString(u.userId) ^ 0x9e37);
  const tr = userTrading(u, from, to);

  // Per-symbol split. Symbols come from the shared helper (not a local draw) so the fills counted here
  // are the very rows mockFillRows hands the fill table.
  const chosen = chosenSymbols(u);
  const fillRows = mockFillRows(u, from, to);
  const fillsBySymbol = new Map<string, number>();
  for (const f of fillRows) fillsBySymbol.set(f.symbol, (fillsBySymbol.get(f.symbol) ?? 0) + 1);
  let remainingTrades = tr.totalTrades;
  let remainingNet = tr.netPnl;
  const bySymbol = chosen.map((symbol, i) => {
    const last = i === chosen.length - 1;
    const t = Math.max(0, last ? remainingTrades : Math.floor(remainingTrades * (0.25 + rng() * 0.35)));
    const n = last ? remainingNet : remainingNet * (0.2 + rng() * 0.5);
    remainingTrades -= t; remainingNet -= n;
    const w = Math.min(t, Math.round(t * (0.4 + rng() * 0.3)));
    const l = Math.max(0, t - w);
    const feeS = t * 2.5;
    const avgW = w > 0 ? Math.abs(n) / w * (0.9 + rng() * 0.5) + 40 : null;
    const avgL = l > 0 ? -(Math.abs(n) / l * (0.5 + rng() * 0.4) + 30) : null;
    return {
      symbol,
      trades: t,
      fills: fillsBySymbol.get(symbol) ?? 0,
      wins: w,
      losses: l,
      netPnl: money(n),
      grossPnl: money(n + feeS),
      fees: money(feeS),
      averageWin: avgW === null ? null : money(avgW),
      averageLoss: avgL === null ? null : money(avgL),
      bestNetPnl: money(Math.abs(n) * (0.4 + rng() * 0.4) + 60),
      worstNetPnl: money(-(Math.abs(n) * (0.3 + rng() * 0.4) + 50)),
    };
  })
    .filter(s => s.trades > 0 || s.fills > 0)
    .sort((a, b) => b.trades - a.trades || b.fills - a.fills || a.symbol.localeCompare(b.symbol));

  // Daily equity curve (cumulative).
  let cum = 0;
  const daily = tr.daily.map(d => {
    cum += d.net;
    return { date: d.date, netPnl: money(d.net), cumulativeNetPnl: money(cum) };
  });

  const account: AdminUserAccountOrNull = u.cqgConnected
    ? {
        account: `OPT-${u.userId.slice(-4).toUpperCase()}`,
        balance: money(25000 + tr.cumulative),
        equity: money(25000 + tr.cumulative + (rng() - 0.5) * 800),
        ote: money((rng() - 0.5) * 1200),
        profitLoss: money(tr.netPnl),
        unrealizedProfitLoss: money((rng() - 0.5) * 1200),
        asOf: new Date().toISOString(),
      }
    : null;

  const positions = u.cqgConnected && tr.openPositions > 0
    ? Array.from({ length: tr.openPositions }, (_, i) => ({
        account: account?.account ?? 'OPT-0000',
        symbol: chosen[i % chosen.length] as string,
        netQty: (rng() > 0.5 ? 1 : -1) * (1 + Math.floor(rng() * 4)),
        avgOpenPrice: (1000 + rng() * 4000).toFixed(2),
        asOf: new Date().toISOString(),
      }))
    : [];

  // Approval audit timeline.
  const audit = [{ action: 'Registered', occurredAt: u.createdAt.toISOString(), actor: u.email }];
  if (u.decidedAt) {
    audit.push({
      action: u.approvalStatus === 'Approved' ? 'Approved' : 'Rejected',
      occurredAt: u.decidedAt.toISOString(),
      actor: 'admin@optimusfutures.com',
    });
  }

  const wins = tr.wins;
  const total = tr.wins + tr.lossCount;
  const winRate = total > 0 ? wins / total : 0;

  const sortedNets = tr.daily.map(d => d.net).filter(n => n !== 0).sort((a, b) => a - b);
  const best = sortedNets.length ? sortedNets[sortedNets.length - 1] as number : null;
  const worst = sortedNets.length ? sortedNets[0] as number : null;

  // Derived KPIs (mirror the backend definitions). Drawdown walks the cumulative daily-net curve.
  let ddPeak = 0, ddCum = 0, maxDd = 0, ddSum = 0, ddN = 0;
  for (const d of tr.daily) {
    ddCum += d.net;
    if (ddCum > ddPeak) ddPeak = ddCum;
    const dip = ddPeak - ddCum;
    if (dip > maxDd) maxDd = dip;
    if (dip > 0) { ddSum += dip; ddN++; }
  }
  const avgWin = tr.wins > 0 ? tr.gains / tr.wins : null;
  const avgLoss = tr.lossCount > 0 ? -(tr.losses / tr.lossCount) : null;
  const profitFactor = tr.losses > 0 ? tr.gains / tr.losses : null;
  const expectancy = tr.totalTrades > 0 ? tr.netPnl / tr.totalTrades : null;
  const payoff = avgWin !== null && avgLoss !== null && avgLoss !== 0 ? avgWin / Math.abs(avgLoss) : null;
  const maxWinStreak = tr.wins > 0 ? 1 + Math.floor(rng() * Math.min(6, tr.wins)) : 0;
  const maxLossStreak = tr.lossCount > 0 ? 1 + Math.floor(rng() * Math.min(5, tr.lossCount)) : 0;

  return {
    profile: {
      userId: u.userId,
      email: u.email,
      firstName: u.firstName,
      lastName: u.lastName,
      phoneNumber: u.phoneNumber,
      timeZone: u.timeZone,
      approvalStatus: u.approvalStatus,
      createdAt: u.createdAt.toISOString(),
      decidedAt: u.decidedAt ? u.decidedAt.toISOString() : null,
      lastActivityAt: tr.lastActivityAt,
      isDeleted: u.isDeleted,
      concurrentLoginEstablished: false, // mockApi overlays the session's toggle state
    },
    trading: {
      trades: tr.totalTrades,
      fills: fillRows.length,
      openPositions: positions.length,
      wins: tr.wins,
      losses: tr.lossCount,
      grossPnl: money(tr.netPnl + tr.fees),
      netPnl: money(tr.netPnl),
      fees: money(tr.fees),
      averageWin: avgWin === null ? null : money(avgWin),
      averageLoss: avgLoss === null ? null : money(avgLoss),
      bestNetPnl: best === null ? null : money(best),
      worstNetPnl: worst === null ? null : money(worst),
      profitFactor: profitFactor === null ? null : profitFactor.toFixed(4),
      expectancy: expectancy === null ? null : money(expectancy),
      payoffRatio: payoff === null ? null : payoff.toFixed(4),
      maxConsecutiveWins: maxWinStreak,
      maxConsecutiveLosses: maxLossStreak,
      maxDrawdown: maxDd > 0 ? money(maxDd) : null,
      averageDrawdown: ddN > 0 ? money(ddSum / ddN) : null,
      recoveryFactor: maxDd > 0 ? (tr.netPnl / maxDd).toFixed(4) : null,
      averageHoldSeconds: tr.totalTrades > 0 ? Math.round(300 + rng() * 7200) : null,
    },
    winRate: winRate.toFixed(4),
    bySymbol,
    daily,
    account,
    positions,
    registrationAudit: audit,
    engagement: {
      lastActivityAt: tr.lastActivityAt,
      activeDaysInRange: tr.activeDays,
    },
    aiCreditBalance: 10, // registration grant; mockApi overlays the session's admin top-ups
    subscription: mockSubscription(u), // mockApi overlays the session's granted/revoked overrides
    accounts: mockAccounts(u, from, to),
    adminActivity: mockAdminActivity(u), // mockApi prepends this session's own admin writes
    aiActivity: mockAiActivity(u, from, to),
    loginActivity: mockLoginActivity(u),
    sessionActivity: mockSessionActivity(u),
  };
}

const AI_STRATEGY_NAMES = [
  'Opening range breakout', 'Gap fade', 'VWAP reversion', 'Trend pullback', 'Overnight inventory',
  'Liquidity sweep', 'Failed auction', 'Momentum ignition',
];

/**
 * A deterministic AI Backtester history. Deliberately varied: some users never touched it, some have a
 * pending credit request, some deleted half their strategies — the states the card has to render well.
 */
function mockAiActivity(u: MockUser, from: string, to: string): AiUserActivity {
  const rng = mulberry32(hashString(u.userId) ^ 0x51a7);
  const day = 86_400_000;
  const base = Date.parse(from + 'T00:00:00Z');

  // Roughly a third of users never opened the backtester — the card must read well empty too.
  const engaged = rng() > 0.35;
  const strategyCount = engaged ? 1 + Math.floor(rng() * 7) : 0;

  const strategies: AiStrategySummary[] = [];
  const ledger: AiCreditLedgerEntry[] = [];
  let runs = 0, runsCompleted = 0, runsFailed = 0, prompts = 0;
  let inputTokens = 0, outputTokens = 0, cost = 0, spent = 0;

  for (let i = 0; i < strategyCount; i++) {
    const createdAt = new Date(base + Math.floor(rng() * 25) * day).toISOString();
    const runCount = Math.floor(rng() * 4);
    const deleted = rng() < 0.25;
    const lastStatus = runCount === 0
      ? null
      : (['Completed', 'Completed', 'Completed', 'Failed'] as const)[Math.floor(rng() * 4)]!;
    const name = AI_STRATEGY_NAMES[(hashString(u.userId) + i) % AI_STRATEGY_NAMES.length]!;

    strategies.push({
      id: `${u.userId}-strat-${i}`,
      name,
      symbols: [SYMBOLS[Math.floor(rng() * SYMBOLS.length)]!],
      timeframe: `${[1, 5, 15, 60][Math.floor(rng() * 4)]} minutes`,
      createdAt,
      deletedAt: deleted ? new Date(Date.parse(createdAt) + 3 * day).toISOString() : null,
      runs: runCount,
      lastRunAt: runCount ? new Date(Date.parse(createdAt) + day).toISOString() : null,
      lastRunStatus: lastStatus,
    });

    runs += runCount;
    if (lastStatus === 'Failed') { runsFailed += 1; runsCompleted += Math.max(0, runCount - 1); }
    else runsCompleted += runCount;

    // Each strategy costs at least one prompt charge; some redeem a half-price clarification retry.
    const charges = 1 + Math.floor(rng() * 3);
    for (let c = 0; c < charges; c++) {
      const isRetry = rng() < 0.25;
      const delta = isRetry ? -0.5 : -1;
      const at = new Date(Date.parse(createdAt) + c * 3600_000).toISOString();
      ledger.push({
        at, delta,
        reason: isRetry ? 5 : 1,
        reasonLabel: isRetry ? 'Clarification retry' : 'Prompt charge',
        strategyId: `${u.userId}-strat-${i}`,
        strategyName: name,
        isRetry,
      });
      spent += -delta;
      prompts += 1;
      const inTok = 800 + Math.floor(rng() * 3000);
      const outTok = 300 + Math.floor(rng() * 1200);
      inputTokens += inTok;
      outputTokens += outTok;
      cost += (inTok / 1e6) * 3 + (outTok / 1e6) * 15;
    }
  }

  // Everyone gets the registration grant; some got an admin top-up.
  const granted = 10 + (engaged && rng() < 0.4 ? 25 : 0);
  ledger.push({
    at: u.createdAt.toISOString(), delta: 10, reason: 0, reasonLabel: 'Registration grant',
    strategyId: null, strategyName: null, isRetry: false,
  });
  if (granted > 10) {
    ledger.push({
      at: new Date(base + 10 * day).toISOString(), delta: 25, reason: 2, reasonLabel: 'Admin grant',
      strategyId: null, strategyName: null, isRetry: false,
    });
  }

  // A pending request = a zero-delta ask with no grant after it (how the journal derives its banner).
  const pending = engaged && spent > granted - 3 && rng() < 0.5;
  if (pending) {
    ledger.push({
      at: new Date(base + 26 * day).toISOString(), delta: 0, reason: 3, reasonLabel: 'Credits requested',
      strategyId: null, strategyName: null, isRetry: false,
    });
  }

  ledger.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  strategies.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));

  const daily = eachDay(from, to).map(date => {
    const onDay = ledger.filter(e => e.at.slice(0, 10) === date && e.delta < 0);
    return {
      date,
      prompts: onDay.length,
      costUsd: money(onDay.length * 0.02),
      creditsSpent: onDay.reduce((s, e) => s + -e.delta, 0),
    };
  });

  return {
    creditBalance: granted - spent,
    creditsGranted: granted,
    creditsSpent: spent,
    creditsRequested: pending ? 1 : 0,
    pendingCreditRequest: pending,
    strategiesCreated: strategies.length,
    strategiesActive: strategies.filter(s => !s.deletedAt).length,
    strategiesDeleted: strategies.filter(s => s.deletedAt).length,
    lastStrategyAt: strategies[0]?.createdAt ?? null,
    runs,
    runsCompleted,
    runsFailed,
    lastRunAt: strategies.find(s => s.lastRunAt)?.lastRunAt ?? null,
    prompts,
    inputTokens,
    outputTokens,
    costUsd: money(cost),
    lastPromptAt: ledger.find(e => e.delta < 0)?.at ?? null,
    strategies,
    ledger,
    daily,
  };
}

/** A seeded admin trail so the timeline card has something to show in the demo. */
function mockAdminActivity(u: MockUser): AdminAuditEntry[] {
  const rng = mulberry32(hashString(u.userId) ^ 0x7241);
  if (rng() < 0.35) return [];   // plenty of users have never been touched by an admin
  const day = 86_400_000;
  const base = u.createdAt.getTime() + 3 * day;
  const actor = 'admin:matt@optimusfutures.com';
  return [
    {
      at: new Date(base + 2 * day).toISOString(), action: 'CreditsGranted', topic: 'credits',
      detail: '+10 credits', actor,
    },
    {
      at: new Date(base).toISOString(), action: 'OverrideGranted', topic: 'override',
      detail: 'Pro · permanent · "comped — early adopter"', actor,
    },
  ];
}

type AdminUserAccountOrNull = AdminUserDetail['account'];

// ─── AI costs ─────────────────────────────────────────────────────────────────────────────
// Per-token USD rates — mirror journal-backend's AiPricingOptions defaults (Claude Opus 4.8 on-demand).
const AI_IN_PER_1M = 5;
const AI_OUT_PER_1M = 25;

/** Deterministic AI-user subset: ~42% of approved users use the AI backtester. */
function aiUsers(): MockUser[] {
  return mockUsers().filter(u => u.approvalStatus === 'Approved' && hashString(u.userId + 'ai') % 100 < 42);
}

export function mockAiCosts(params: AiCostsParams): AiCosts {
  const { from, to, page, pageSize, search, sortBy = 'cost', sortDir = 'desc' } = params;
  const days = eachDay(from, to);
  const dailyAgg = new Map<string, { cost: number; prompts: number }>(days.map(d => [d, { cost: 0, prompts: 0 }]));

  const rows: AiUserUsageRow[] = [];
  let totalCost = 0, totalPrompts = 0, totalIn = 0, totalOut = 0;

  for (const u of aiUsers()) {
    const rng = mulberry32(hashString(u.userId + 'aiusage'));
    let prompts = 0, inTok = 0, outTok = 0, cost = 0;
    let lastUsed: string | null = null;
    for (const date of days) {
      if (rng() > 0.16) continue; // most days have no AI activity
      const n = 1 + Math.floor(rng() * 3);
      for (let i = 0; i < n; i++) {
        const input = 1200 + Math.floor(rng() * 3200);
        const output = 250 + Math.floor(rng() * 1700);
        const c = (input / 1e6) * AI_IN_PER_1M + (output / 1e6) * AI_OUT_PER_1M;
        prompts++; inTok += input; outTok += output; cost += c;
        const dd = dailyAgg.get(date)!; dd.cost += c; dd.prompts++;
        lastUsed = new Date(date + 'T14:30:00').toISOString();
      }
    }
    if (prompts === 0) continue;
    totalCost += cost; totalPrompts += prompts; totalIn += inTok; totalOut += outTok;
    rows.push({
      userId: u.userId,
      email: u.email,
      prompts,
      inputTokens: inTok,
      outputTokens: outTok,
      totalTokens: inTok + outTok,
      costUsd: money(cost),
      // Balance = Σ ledger deltas: 10 free + an occasional admin top-up − prompts used. Floored at 0.
      creditBalance: Math.max(0, 10 + (prompts > 6 ? 20 : 0) - prompts),
      lastUsed,
      isDisabled: false,
    });
  }

  const activeUsers = rows.length;
  const summary: AiCostsSummary = {
    totalCostUsd: money(totalCost),
    totalPrompts,
    totalInputTokens: totalIn,
    totalOutputTokens: totalOut,
    activeUsers,
    avgCostPerUser: money(activeUsers > 0 ? totalCost / activeUsers : 0),
    avgCostPerPrompt: money(totalPrompts > 0 ? totalCost / totalPrompts : 0),
    // Mock the AWS Cost Explorer figure a bit above the recorded total (represents all-in Bedrock spend,
    // incl. usage before per-prompt recording started). Month start = first of the range-end's month.
    bedrockMtdCostUsd: money(totalCost * 1.2 + 3),
    bedrockCostMonthStart: `${to.slice(0, 7)}-01`,
  };
  const costDaily: AiCostDaily[] = days.map(d => {
    const dd = dailyAgg.get(d)!;
    return { date: d, costUsd: money(dd.cost), prompts: dd.prompts };
  });

  const term = search?.trim().toLowerCase();
  const filtered = term ? rows.filter(r => r.email.toLowerCase().includes(term)) : rows;
  const sorted = [...filtered].sort((a, b) => aiCmp(a, b, sortBy) * (sortDir === 'asc' ? 1 : -1));
  const total = sorted.length;
  const start = (page - 1) * pageSize;
  const items = sorted.slice(start, start + pageSize);
  return { from, to, summary, costDaily, users: { items, total, page, pageSize } };
}

function aiCmp(a: AiUserUsageRow, b: AiUserUsageRow, key: AiUsageSortBy): number {
  switch (key) {
    case 'prompts':  return a.prompts - b.prompts;
    case 'tokens':   return a.totalTokens - b.totalTokens;
    case 'lastUsed': return new Date(a.lastUsed ?? 0).getTime() - new Date(b.lastUsed ?? 0).getTime();
    case 'email':    return a.email.localeCompare(b.email);
    case 'cost':
    default:         return Number(a.costUsd) - Number(b.costUsd);
  }
}


/**
 * Accounts for the dropdown: everyone has a Manual account (journal seeds one on provisioning); CQG-connected
 * users also get a CQG account with a live balance + positions, and roughly a third get a Rithmic one (fills
 * only, no balance feed) so the "no live balance" branch is demoable.
 */
export function mockAccounts(u: MockUser, from: string, to: string): AdminUserAccount[] {
  const rng = mulberry32(hashString(u.userId + 'accounts'));
  const tr = userTrading(u, from, to);
  // Counted from the generated executions rather than invented, so Σ per-account fills = the Fills KPI.
  const fillsByAccount = new Map<string, number>();
  for (const f of mockFillRows(u, from, to))
    fillsByAccount.set(f.account, (fillsByAccount.get(f.account) ?? 0) + 1);
  const accounts: AdminUserAccount[] = [{
    id: `acct-${u.userId}-manual`,
    name: 'Manual',
    provider: 'Manual',
    displayName: null,
    brokerageAccountNumber: null,
    environment: null,
    createdAt: u.createdAt.toISOString(),
    live: null,
    positions: [],
    trades: Math.round(tr.totalTrades * 0.15),
    fills: fillsByAccount.get('Manual') ?? 0,
    netPnl: money(tr.netPnl * 0.1),
  }];

  if (u.cqgConnected) {
    const acctId = cqgAccountName(u);
    const balance = 25000 + tr.cumulative;
    accounts.push({
      id: `acct-${u.userId}-cqg`,
      name: acctId,
      provider: 'CQG',
      displayName: `Ironbeam — ${u.firstName} ${u.lastName}`,
      brokerageAccountNumber: `IB-${acctId.slice(-5)}`,
      environment: rng() > 0.5 ? 'live' : 'demo',
      createdAt: u.createdAt.toISOString(),
      live: {
        account: acctId,
        balance: money(balance),
        equity: money(balance + (rng() - 0.5) * 400),
        ote: money((rng() - 0.5) * 600),
        profitLoss: money(tr.netPnl),
        unrealizedProfitLoss: money((rng() - 0.5) * 600),
        // Some users last connected days ago, so the stale-balance pill is demoable.
        asOf: new Date(Date.now() - (rng() > 0.6 ? 3 * 86_400_000 : 2 * 3_600_000)).toISOString(),
        currency: 'USD',
        totalMargin: money(800 + rng() * 2400),
        purchasingPower: money(balance * (0.8 + rng() * 0.15)),
      },
      positions: tr.openPositions > 0
        ? Array.from({ length: tr.openPositions }, (_, i) => ({
            account: acctId,
            symbol: (SYMBOLS[(hashString(u.userId) + i) % SYMBOLS.length] ?? 'ES'),
            netQty: (rng() > 0.4 ? 1 : -1) * (1 + Math.floor(rng() * 4)),
            avgOpenPrice: (1000 + rng() * 4000).toFixed(2),
            asOf: new Date().toISOString(),
          }))
        : [],
      trades: Math.round(tr.totalTrades * 0.85),
      fills: fillsByAccount.get(acctId) ?? 0,
      netPnl: money(tr.netPnl * 0.9),
    });
  }

  if (hasRithmic(u)) {
    const rithId = rithmicAccountName(u);
    accounts.push({
      id: `acct-${u.userId}-rithmic`,
      name: rithId,
      provider: 'Rithmic',
      displayName: 'Rithmic paper',
      brokerageAccountNumber: null,
      environment: 'demo',
      createdAt: u.createdAt.toISOString(),
      live: null,          // Rithmic ships fills only — no balance/position feed
      positions: [],
      trades: 0,
      fills: fillsByAccount.get(rithId) ?? 0,
      netPnl: money(0),
    });
  }

  return accounts;
}

// ─── Subscriptions ──────────────────────────────────────────────────────────────────────
/** The free base tier's sentinel period end (journal-backend pins free/manual rows to 2099). */
const FREE_PERIOD_END = '2099-12-31T00:00:00.0000000+00:00';

/**
 * Deterministic subscription per user, so the demo shows every state worth designing for:
 * ~1 in 6 is a paying Pro (with payments), 1 in 12 carries an active override, 1 in 18 is PastDue
 * (never entitled — no grace period), and everyone else is the free Core/Manual/2099 row.
 */
export function mockSubscription(u: MockUser): SubscriptionSummary | null {
  if (u.approvalStatus !== 'Approved') return null; // unapproved users never got provisioned a row
  const seed = hashString(u.userId + 'sub');
  const rng = mulberry32(seed);
  const isPro = seed % 6 === 0;
  const isPastDue = seed % 18 === 5;
  const hasOverride = seed % 12 === 3;

  const now = Date.now();
  const periodEnd = isPro || isPastDue
    ? new Date(now + Math.floor(rng() * 26 + 4) * 86400_000).toISOString()
    : FREE_PERIOD_END;
  const subscriptionTier = isPro || isPastDue ? 'Pro' : 'Core';
  const status = isPastDue ? 'PastDue' : 'Active';
  const override = hasOverride
    ? {
        // An override on a free user upgrades them; on a paying Pro it holds them down to Core.
        tier: (subscriptionTier === 'Pro' ? 'Core' : 'Pro') as 'Core' | 'Pro',
        expiresAt: seed % 24 === 3 ? new Date(now + 30 * 86400_000).toISOString() : null,
        reason: subscriptionTier === 'Pro' ? 'Billing dispute — temporary hold' : 'Partner comp — Optimus Futures',
        grantedBy: 'admin:liron',
        grantedAt: new Date(now - Math.floor(rng() * 20 + 1) * 86400_000).toISOString(),
      }
    : null;

  const payments = isPro || isPastDue
    ? Array.from({ length: 1 + Math.floor(rng() * 4) }, (_, i) => ({
        provider: 'stripe',
        providerPaymentId: `in_${(seed + i).toString(36)}${i}`,
        paidAt: new Date(now - (i + 1) * 30 * 86400_000).toISOString(),
        amountCents: 3000,
        currency: 'usd',
      }))
    : [];

  return {
    tier: (override?.tier ?? subscriptionTier) as 'Core' | 'Pro',
    subscriptionTier: subscriptionTier as 'Core' | 'Pro',
    status: status as SubscriptionSummary['status'],
    source: subscriptionTier === 'Pro' ? 'Stripe' : 'Manual',
    currentPeriodEnd: periodEnd,
    cancelAtPeriodEnd: isPro && seed % 30 === 6,
    // Mirrors the real formula: an active override always entitles; PastDue never does.
    isEntitled: override != null || status === 'Active',
    override,
    payments,
  };
}

/** The seeded plan catalog — Core displayed as "Free" (re-priceable) and the featured Pro tier. */
export function mockPlans(): AdminPlan[] {
  const now = new Date().toISOString();
  return [
    {
      id: 'plan-core', tier: 'Core', name: 'Free',
      description: 'The full trading journal, free forever',
      features: ['All journal features', 'CQG + Rithmic + CSV import', 'Statistics and calendar'],
      monthlyPriceCents: 0, annualPriceCents: 0, currency: 'usd',
      stripePriceIdMonthly: null, stripePriceIdAnnual: null, stripeProductId: null,
      aiCreditsPerMonth: 0, isActive: true, isFeatured: false, sortOrder: 1, updatedAt: now,
    },
    {
      id: 'plan-pro', tier: 'Pro', name: 'Pro',
      description: 'Everything in Free plus AI',
      features: ['Everything in Free', 'AI backtesting', 'Prop-firm evaluation'],
      monthlyPriceCents: 3000, annualPriceCents: 28800, currency: 'usd',
      stripePriceIdMonthly: 'price_1MockMonthly', stripePriceIdAnnual: 'price_1MockAnnual',
      stripeProductId: 'prod_MockPro',
      aiCreditsPerMonth: 250, isActive: true, isFeatured: true, sortOrder: 2, updatedAt: now,
    },
  ];
}

/**
 * The seeded one-time credit-pack catalog. Packs have no Stripe Price object (checkout uses inline
 * `price_data`), so `priceCents` is exactly the charge — hence no `stripePriceId` field to mock.
 */
export function mockCreditPacks(): AdminCreditPack[] {
  const now = new Date().toISOString();
  return [
    {
      id: 'pack-small', code: 'credits_10', name: '10 credits',
      description: 'A quick top-up', credits: 10, priceCents: 999, currency: 'usd',
      stripeProductId: 'prod_MockPacks', isActive: true, sortOrder: 1, updatedAt: now,
    },
    {
      id: 'pack-medium', code: 'credits_50', name: '50 credits',
      description: 'Best value', credits: 50, priceCents: 3999, currency: 'usd',
      stripeProductId: 'prod_MockPacks', isActive: true, sortOrder: 2, updatedAt: now,
    },
    {
      id: 'pack-large', code: 'credits_200', name: '200 credits',
      description: 'For heavy backtesting', credits: 200, priceCents: 13999, currency: 'usd',
      stripeProductId: 'prod_MockPacks', isActive: false, sortOrder: 3, updatedAt: now,
    },
  ];
}

// ─── Infra observability ────────────────────────────────────────────────────────────────
const INFRA_WINDOWS: Record<InfraWindow, { spanMs: number; period: number }> = {
  '1h':  { spanMs: 3_600_000,      period: 60 },
  '6h':  { spanMs: 6 * 3_600_000,  period: 60 },
  '24h': { spanMs: 24 * 3_600_000, period: 300 },
  '3d':  { spanMs: 3 * 86_400_000, period: 900 },
};

/** Healthy dev: API 2/2 + worker 1/1, 2 healthy targets, a recent scale-out→in pair, empty queue. */
export function mockInfraOverview(): InfraOverview {
  const now = Date.now();
  return {
    status: {
      services: [
        { name: 'journal-dev-api', running: 2, desired: 2, events: ['(service journal-dev-api) has reached a steady state.'] },
        { name: 'journal-dev-api-worker', running: 1, desired: 1, events: ['(service journal-dev-api-worker) has reached a steady state.'] },
      ],
      healthyTargets: 2,
      totalTargets: 2,
      targets: [
        { id: 'i-0a1b2c3d', state: 'healthy', reason: null },
        { id: 'i-0e4f5a6b', state: 'healthy', reason: null },
      ],
      scalingActivities: [
        { at: new Date(now - 4 * 60_000).toISOString(), service: 'journal-dev-api', description: 'Setting desired count to 2.', cause: 'monitor alarm TargetTracking/CPUUtilization < 60 for 3 datapoints', status: 'Successful' },
        { at: new Date(now - 9 * 60_000).toISOString(), service: 'journal-dev-api', description: 'Setting desired count to 3.', cause: 'monitor alarm TargetTracking/CPUUtilization > 60 for 3 datapoints', status: 'Successful' },
      ],
    },
    health: { queueDepth: 0, oldestWaitSeconds: 0, stuckRunning: 0, failed24h: 0, recentFailures: [] },
  };
}

/** Metric series where the WORKER CPU spikes (backtests) while the API stays flat — the isolation proof. */
export function mockInfraMetrics(window: InfraWindow): InfraMetrics {
  const { spanMs, period } = INFRA_WINDOWS[window];
  const end = Date.now();
  const start = end - spanMs;
  const n = Math.min(500, Math.max(2, Math.floor(spanMs / (period * 1000))));
  const timestamps = Array.from({ length: n }, (_, i) => new Date(start + i * period * 1000).toISOString());
  const rng = mulberry32(hashString('infra' + window));

  const s = (id: string, label: string, gen: (i: number) => number): MetricSeries =>
    ({ id, label, timestamps, values: timestamps.map((_, i) => Number(gen(i).toFixed(2))) });
  const wkr = (i: number) => (Math.sin(i / 6) * 0.5 + 0.5 > 0.8 ? 58 + rng() * 27 : 4 + rng() * 8);

  const series: MetricSeries[] = [
    s('apiCpuAvg', 'API CPU avg', () => 8 + rng() * 6),
    s('apiCpuMax', 'API CPU max', () => 14 + rng() * 8),
    s('wkrCpuAvg', 'Worker CPU avg', wkr),
    s('wkrCpuMax', 'Worker CPU max', (i) => Math.min(98, wkr(i) + rng() * 10)),
    s('apiMemAvg', 'API memory avg', () => 52 + rng() * 5),
    s('wkrMemAvg', 'Worker memory avg', () => 47 + rng() * 7),
    s('elbHealthy', 'Healthy hosts', () => 2),
    s('elbUnhealthy', 'Unhealthy hosts', () => 0),
    s('elbP95', 'p95 response (s)', () => 0.04 + rng() * 0.12),
    s('elb5xxTarget', 'Target 5XX', () => 0),
    s('elb5xxElb', 'ELB 5XX', () => 0),
    s('elbRequests', 'Requests', () => Math.floor(rng() * 40)),
  ];

  const today = ymd(new Date(end));
  const runCharts: BacktestCharts = {
    daily: [{ date: today, completed: 12, failed: 0 }],
    durations: [{ date: today, avgSeconds: 3.4, maxSeconds: 8.1 }],
    coarsenedRuns: 2,
    totalRuns: 14,
  };
  return { window, periodSeconds: period, series, runCharts };
}

// ─── Per-strategy AI chat history (dev only) ──────────────────────────────────────────

/**
 * Seeded "Ask the AI" exchanges. Each entry is one turn template: what the user asked, what came
 * back, and — for the turns that propose something — the boxes it offered to rewrite. Written as
 * plausible backtester talk so the panel can be judged on real-shaped content rather than lorem.
 */
const CHAT_SCRIPT: readonly {
  question: string;
  reply: string;
  reasoning: string | null;
  proposal?: { question: string; changes: { target: string; replacement: string }[] };
  asks?: { text: string; options: string[] }[];
}[] = [
  {
    question: 'Why did this only take 3 trades in six months?',
    reply:
      'Your entry needs the 20 EMA reclaim AND volume above the 30-day average in the same bar. On '
      + 'ES those line up about four times a year. Relaxing either condition to "within 3 bars" would '
      + 'give you roughly 40 setups over the same window.',
    reasoning:
      'Counted bars satisfying each condition separately (612 and 488) versus jointly (3) — the '
      + 'conjunction, not either threshold on its own, is what makes this rare.',
  },
  {
    question: 'Can you make it exit faster when the trade goes against me?',
    reply:
      'Yes. Right now the exit only fires on the opposite signal, so a losing trade rides to the end '
      + 'of the session. A hard stop at 1.5x the 14-period ATR cuts the worst loss in your sample from '
      + '-$2,140 to about -$680.',
    reasoning: 'Worst drawdown in the run came from one unstopped trade held 4h20m through a trend reversal.',
    proposal: {
      question: 'Add an ATR-based stop to the exit rule?',
      changes: [
        { target: 'exit', replacement: 'Exit on opposite signal, or when price moves 1.5x ATR(14) against entry.' },
      ],
    },
    asks: [{ text: 'How tight do you want the stop?', options: ['1x ATR', '1.5x ATR', '2x ATR'] }],
  },
  {
    question: 'What does "failed auction" mean in the results?',
    reply:
      'It is the label your own strategy name gave the setup — the engine does not interpret it. In '
      + 'your rules it resolves to: price trades outside the prior session value area and closes back '
      + 'inside within two bars.',
    reasoning: null,
  },
  {
    question: 'Try it on NQ as well and tighten the entry',
    reply:
      'Adding NQ roughly doubles the trade count but the win rate drops from 58% to 49% — NQ moves '
      + 'faster than your 15-minute timeframe resolves. I would tighten the entry before widening the '
      + 'symbol list, not at the same time.',
    reasoning:
      'Ran the same rules against NQ bars: more signals, worse follow-through at this timeframe. '
      + 'Changing two variables at once would leave the cause of any change ambiguous.',
    proposal: {
      question: 'Apply the tighter entry and the expanded description?',
      changes: [
        { target: 'entry', replacement: 'Enter on 20 EMA reclaim with volume > 1.5x the 30-day average.' },
        { target: 'description', replacement: 'Momentum reclaim, ES only, 15m. Entry requires a volume confirmation.' },
      ],
    },
    asks: [{ text: 'Add NQ now or after the entry change is validated?', options: ['Now', 'After'] }],
  },
];

const CHAT_PROMPT_VERSIONS = ['chat-v3', 'chat-v4', 'chat-v4.1'] as const;
const CHAT_DECISION_VALUES = ['applied', 'declined'] as const;

/**
 * One strategy's chat turns, oldest first. Deterministic in `(userId, strategyId)`, so re-opening the
 * panel shows the same conversation.
 *
 * Most strategies have **no** chat — the Ask-the-AI box is optional, and only turns made after the
 * strategy was saved are ever persisted (pre-save turns are session-only in the trader app). The
 * mock reproduces that so the empty state is the common path here too, not an afterthought.
 */
export function mockStrategyChat(userId: string, strategyId: string): AdminStrategyChat {
  const rng = mulberry32(hashString(`${userId}|${strategyId}`) ^ 0xc47a);

  // ~45% of strategies were never asked about.
  if (rng() < 0.45) return { strategyId, turns: [] };

  const turnCount = 1 + Math.floor(rng() * CHAT_SCRIPT.length);
  const start = Date.parse('2026-08-31T09:12:00Z') + Math.floor(rng() * 6) * 86_400_000;
  const promptVersion = CHAT_PROMPT_VERSIONS[Math.floor(rng() * CHAT_PROMPT_VERSIONS.length)]!;

  const turns: AdminChatTurn[] = [];
  for (let i = 0; i < turnCount; i++) {
    const script = CHAT_SCRIPT[(hashString(strategyId) + i) % CHAT_SCRIPT.length]!;
    const createdAt = new Date(start + i * (7 * 60_000 + Math.floor(rng() * 900_000))).toISOString();

    // A proposal is only *decided* some of the time — an undecided turn is a real, renderable state.
    const decided = !!script.proposal && rng() < 0.7;
    const decisions: Record<string, string> = {};
    if (decided) {
      for (const c of script.proposal!.changes) {
        decisions[c.target] = CHAT_DECISION_VALUES[rng() < 0.65 ? 0 : 1]!;
      }
    }

    turns.push({
      id: `${strategyId}-turn-${i}`,
      createdAt,
      question: script.question,
      reply: script.reply,
      reasoning: script.reasoning,
      promptVersion,
      proposalQuestion: script.proposal?.question ?? null,
      proposalChanges: script.proposal?.changes ?? [],
      questions: script.asks ?? [],
      decisions,
      decidedAt: decided ? new Date(Date.parse(createdAt) + 45_000).toISOString() : null,
    });
  }

  return { strategyId, turns };
}

// ─── Sign-in history ────────────────────────────────────────────────────────────────────

/**
 * Deterministic sign-in history for mock mode.
 *
 * Deliberately models the real constraint rather than an idealised one: Cognito only started recording
 * when threat protection was switched on (2026-09-15), so `recordingSince` is that date and every user
 * has a SHORT history — no lifetime totals, and some users legitimately have none at all. Building
 * mocks that show years of logins would make the card look right here and wrong in production.
 */
function mockLoginActivity(u: MockUser): LoginActivity {
  const rng = mulberry32(hashString(u.userId + 'logins'));
  const recordingSince = '2026-09-15';
  const since = new Date(recordingSince + 'T09:00:00Z').getTime();
  const now = Date.now();

  // ~1 in 6 users has not signed in since recording began — the realistic empty state.
  const count = rng() < 0.17 ? 0 : 1 + Math.floor(rng() * 14);

  const devices = [
    'Chrome 125, Windows 10', 'Safari 17, macOS 14', 'Chrome 126, macOS 14',
    'Edge 125, Windows 11', 'Safari Mobile 17, iOS 17',
  ];
  const places: [string, string][] = [
    ['Chicago', 'United States'], ['New York', 'United States'], ['Austin', 'United States'],
    ['London', 'United Kingdom'], ['Tel Aviv', 'Israel'],
  ];

  const events: LoginEvent[] = [];
  for (let i = 0; i < count; i++) {
    const at = new Date(since + rng() * Math.max(now - since, 1));
    const [city, country] = places[Math.floor(rng() * places.length)]!;
    // A failure now and then — a locked-out user is exactly what an operator opens this card to see.
    const failed = rng() < 0.12;
    events.push({
      id: `${u.userId}-login-${i}`,
      at: at.toISOString(),
      ip: `203.0.113.${1 + Math.floor(rng() * 250)}`,
      device: devices[Math.floor(rng() * devices.length)]!,
      city,
      country,
      risk: rng() < 0.9 ? 'NoRisk' : 'Low',
      outcome: failed ? 'Fail' : 'Pass',
    });
  }
  events.sort((a, b) => b.at.localeCompare(a.at));

  const successes = events.filter(e => e.outcome === 'Pass');
  return {
    totalLogins: successes.length,
    truncated: false,
    lastLoginAt: successes[0]?.at ?? null,
    recordingSince,
    events,
  };
}

function mockSessionActivity(u: MockUser): SessionActivity {
  const rng = mulberry32(hashString(u.userId + 'sessions'));
  const now = Date.now();
  const items: SessionItem[] = [];
  for (let day = 0; day < 20; day++) {
    if (rng() < 0.4) continue;
    const start = now - day * 86_400_000 - Math.floor(rng() * 8) * 3_600_000;
    const renewals = Math.floor(rng() * 5);
    const password = rng() < 0.15;
    items.push({
      startedAt: new Date(start).toISOString(),
      startedBy: password ? 'password' : 'saved-session',
      lastSeenAt: new Date(start + renewals * 3_540_000).toISOString(),
      renewals,
      atLeastMinutes: renewals * 59,
      ip: password ? '198.51.100.7' : null,
      device: password ? 'Chrome 125, Windows 10' : null,
      city: password ? 'Chicago' : null,
      country: password ? 'United States' : null,
    });
  }
  return {
    recordingSince: MOCK_SESSIONS_SINCE, windowDays: 90, sessions: items.length,
    passwordSessions: items.filter(i => i.startedBy === 'password').length,
    savedSessionSessions: items.filter(i => i.startedBy === 'saved-session').length,
    lastSeenAt: items[0]?.lastSeenAt ?? null, truncated: false, items,
  };
}
